import React, { useEffect } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'

import { Box, Button, Flex, Heading, VStack } from '@chakra-ui/react'

import { FileInput, Input, Select } from '@/components/Form'
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import useUser from '@/features/users/useUser'

//API

//Constant
import { useContributorGroup } from '@/features/contributorGroups/useContributorGroup'
import { useCostCenter } from '@/features/costCenters/useCostCenter'
import useVendor from '@/features/vendors/useVendor'
import { SystemVariableType } from '@/features/systemVariables/constants'
import useSystemVariable from '@/features/systemVariables/useSystemVariable'
import { camelToWords } from '@/utils/stringHelper'
import { UserEditRight, UserRole, UserStatus } from '../../constants'

const UserForm = ({ user, onSubmitAction, type, isCreate = true }) => {
  const methods = useForm({ defaultValues: user })

  const { useContributorGroupOptions } = useContributorGroup()
  const { useCostCenterOptions } = useCostCenter()
  const { useFetchAllUsers } = useUser()
  const { useSystemVariableOptions } = useSystemVariable()
  const { useVendorOptions } = useVendor()
  const { useUserOptions } = useUser()

  const teams = useWatch({ name: 'departments', control: methods.control })
  const defaultTeam = useWatch({ name: 'defaultDepartment', control: methods.control })
  const role = useWatch({ name: 'role', control: methods.control })
  const createIdMethod = useWatch({ name: 'createIdMethod', control: methods.control })

  //Translated Data
  const { data: costCenterOptions } = useCostCenterOptions({ status: 'active', labelType: 'detail', valueKey: '_id' })
  const { data: contributorGroupsOptions } = useContributorGroupOptions({ status: 'active' })
  const { data: teamOptions } = useSystemVariableOptions(SystemVariableType.TEAM, { status: 'active', valueKey: '_id' })
  const { data: vendorOptions } = useVendorOptions({ status: 'active', valueKey: '_id' })
  const { data: businessUnitOptions } = useSystemVariableOptions(SystemVariableType.BUSINESS_UNIT, {
    status: 'active',
    valueKey: '_id',
  })
  const { data: technicalUserOptions } = useUserOptions({
    valueKey: '_id',
    status: 'active',
    contributorGroup: ContributorGroupType.TECHNICAL_PROJECT_MANAGER,
  })

  const { data: commercialUserOptions } = useUserOptions({
    valueKey: '_id',

    status: 'active',
    contributorGroup: ContributorGroupType.COMMERCIAL_PROJECT_MANAGER,
  })

  useEffect(() => {
    if (teams && teamOptions) {
      if (!teams.includes(defaultTeam)) {
        methods.setValue('defaultDepartment', '', { shouldDirty: true })
      }
    }
  }, [teams, teamOptions, defaultTeam, methods])

  useEffect(() => {
    if (user && user?._id) {
      methods.reset(user)
    }
  }, [user, methods])

  const onSubmit = async (data) => {
    if (data?.signature?._id) {
      data.signature = data.signature._id
    } else {
      delete data.signature
    }

    if (data.password === '') {
      delete data.password
    }

    // console.log(data)
    onSubmitAction(data)
  }

  return (
    <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} autoComplete="off">
          <Flex p={5} borderBottom="1px solid #d8dbe0">
            <Heading> User Information </Heading>
          </Flex>
          <Box p={5}>
            <VStack>
              {isCreate && (
                <Select
                  name="createIdMethod"
                  label="Create ID Method"
                  disabled={!!role && !isCreate}
                  options={[
                    {
                      label: 'Auto',
                      value: 'auto',
                    },
                    {
                      label: 'Manual',
                      value: 'manual',
                    },
                  ]}
                  isRow
                />
              )}

              {(!isCreate || createIdMethod === 'manual') && (
                <Input
                  name="employeeNumber"
                  label={type !== UserRole.VENDOR ? 'Employee Number' : 'Vendor ID'}
                  type="text"
                  placeholder="Enter value"
                  isRow
                  disabled={!isCreate}
                />
              )}

              {type !== UserRole.VENDOR && (
                <Input
                  name="processId"
                  label={'Process ID'}
                  type="text"
                  placeholder="Enter value"
                  isRow
                  isRequired={false}
                  autoComplete="new-username"
                />
              )}
              <Input name="email" label="Email" type="text" placeholder="Enter value" isRow disabled={!isCreate} />
              <Input
                name="password"
                label="Password"
                type="password"
                placeholder="Enter value"
                isRow
                isRequired={isCreate}
                autoComplete="new-password"
              />
              <Input name="firstName" label="First Name" type="text" placeholder="Enter value" isRow />
              <Input name="lastName" label="Last Name" type="text" placeholder="Enter value" isRow />
              <Input
                name="contactNumber"
                label="Contact Number"
                type="text"
                placeholder="Enter value"
                isRow
                isRequired={false}
              />

              <Select
                name="role"
                label="Role"
                autoComplete="false"
                disabled={!!role && !isCreate}
                options={
                  type === UserRole.VENDOR
                    ? [
                        {
                          label: camelToWords(UserRole.VENDOR),
                          value: UserRole.VENDOR,
                        },
                      ]
                    : [
                        {
                          label: camelToWords(UserRole.ADMIN),
                          value: UserRole.ADMIN,
                        },
                        {
                          label: camelToWords(UserRole.USER),
                          value: UserRole.USER,
                        },
                      ]
                }
                isRow
              />
              <Select
                name="status"
                label="Status"
                autoComplete="false"
                defaultValue={UserStatus.ACTIVE}
                options={Object.values(UserStatus).map((status) => {
                  return {
                    label: camelToWords(status),
                    value: status,
                  }
                })}
                isRow
              />

              {type !== UserRole.VENDOR && (
                <>
                  <Input
                    name="initial"
                    label="Initial"
                    type="text"
                    placeholder="Enter value"
                    isRow
                    isRequired={false}
                  />
                  <Input name="title" label="Title" type="text" placeholder="Enter value" isRow isRequired={false} />
                  <Select
                    name="businessUnit"
                    label="Business Unit"
                    placeholder="Enter value"
                    options={businessUnitOptions}
                    isRow
                    isRequired={false}
                  />

                  <Input
                    name="division"
                    label="Division"
                    type="text"
                    placeholder="Enter value"
                    isRow
                    isRequired={false}
                  />

                  <Select
                    name="editRights"
                    label="Admin Role"
                    options={Object.values(UserEditRight).map((editRight) => {
                      return {
                        value: editRight,
                        label: camelToWords(editRight),
                      }
                    })}
                    isRow
                    isMulti
                    isRequired={false}
                  />
                  <Select
                    name="contributorGroup"
                    label="Contributor Group"
                    options={contributorGroupsOptions}
                    isRow
                    isRequired={false}
                  />
                  <Input name="level" label="Level" type="number" placeholder="Enter value" isRow isRequired={false} />

                  <Select name="departments" label="Teams" options={teamOptions} isMulti isRow isRequired={false} />

                  <Select
                    name="defaultDepartment"
                    label="Default Teams"
                    options={teamOptions}
                    isRow
                    isRequired={false}
                  />
                  <Select name="costCenter" label="Cost Center" options={costCenterOptions} isRow isRequired={false} />
                  <Select
                    name="technicalLineManager"
                    label="Technical Line Manager"
                    options={technicalUserOptions}
                    isRow
                    isRequired={false}
                  />
                  <Select
                    name="commercialLineManager"
                    label="Commercial Line Manager"
                    options={commercialUserOptions}
                    isRow
                    isRequired={false}
                  />

                  <FileInput
                    label="Signature"
                    name="signature"
                    fileType="USER_SIGNATURE"
                    canUpload
                    isRequired={false}
                    isRow
                    accept={'.png,.jpg'}
                  />
                </>
              )}

              {type === UserRole.VENDOR && <Select name="supplier" label="Supplier" options={vendorOptions} isRow />}
            </VStack>
            <Button mt="6" colorScheme="blue" type="submit">
              Save
            </Button>
          </Box>
        </form>
      </FormProvider>
    </Box>
  )
}

export default UserForm
